import { useMediaManagerSDK } from './useMediaManagerSDK';
import { useMemo } from 'react';
import { useMMGRSettings } from '../hoc/withMMGRSettings';
import { HttpClient } from '@wix/http-client';
import { createMediaManagerApi } from '@wix/media-manager-http-api';
import type { MediaManagerApi, MediaManagerApiMethod } from '@wix/media-manager-http-api';
import type { IUseMediaManagerSDK } from './useMediaManagerSDK/useMediaManagerSDK';
import { captureException } from '@sentry/react';
import { fedopsLogger } from '../../services/loggers/fedopsLogger';
import { FedOpsInteraction } from '../../constants/FedOpsInteraction';
import type { ErrorWithData } from '../../monitoring/sentry';

export { MediaManagerApi };

const parsingInteractions: Record<MediaManagerApiMethod, FedOpsInteraction> = {
  listItems: FedOpsInteraction.ParseListItems,
};

const requestsInteractions: Record<MediaManagerApiMethod, FedOpsInteraction> = {
  listItems: FedOpsInteraction.PlatformizedListItems,
};

export const useMediaManagerApi = (params: IUseMediaManagerSDK): MediaManagerApi => {
  const mediaManagerSDK = useMediaManagerSDK(params);
  const settings = useMMGRSettings();
  const {
    mmgrSettings: { metaSiteToken, namespace, isPlatformizationTypesCheckEnabled, isPlatformizedApiEnabled },
  } = settings;

  const source = params.source;
  const debug = process.env.NODE_ENV === 'development';

  const httpClient = useMemo<HttpClient | null>(() => {
    if (!metaSiteToken) {
      captureException(new Error('[useMediaManagerApi] metaSiteToken is required'));
      return null;
    }

    return new HttpClient({
      baseURL: '/_api/',
      getAppToken: () => metaSiteToken,
    });
  }, [metaSiteToken]);

  return useMemo<MediaManagerApi>(() => {
    const logParsingStarted = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionStarted(parsingInteractions[methodName]);
    };

    const logParsingEnded = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionEnded(parsingInteractions[methodName]);
    };

    const logPlatformizedRequest = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionStarted(requestsInteractions[methodName]);
    };

    const logPlatformizedResponse = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionEnded(requestsInteractions[methodName]);
    };

    return createMediaManagerApi({
      experiments: {
        typeValidationEnabled: isPlatformizationTypesCheckEnabled,
        platformizedApiEnabled: isPlatformizedApiEnabled,
      },
      namespace: namespace || undefined,
      httpClient,
      mediaManagerSDK,
      onError: (error, data) => {
        (error as ErrorWithData).data = data;
        captureException(error);
      },
      source,
      debug,
      monitoring: {
        onParsingStarted: logParsingStarted,
        onParsingEnded: logParsingEnded,
        onPlatformizedRequest: logPlatformizedRequest,
        onPlatformizedResponse: logPlatformizedResponse,
      },
    });
  }, [
    debug,
    httpClient,
    isPlatformizationTypesCheckEnabled,
    isPlatformizedApiEnabled,
    mediaManagerSDK,
    namespace,
    source,
  ]);
};
